import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { TextArea } from "components/Form/FormikControls";
import { withNamespace } from "components/Form/FormikControls/lib";
import { Column } from "components/Layout";
import {
  ReviewCard,
  ReviewCardTitle,
  ReviewField,
  ReviewFieldGrid,
} from "components/ReviewCard";
import { LivestockSalePricingTypeDisplayNameLkp } from "constants/pricingTypes";

import {
  calculateTotalPriceCents,
  formatDollarsFromCents,
  formatWeightKg,
} from "lib";

import {
  getBusinesses,
  getCategories,
  getConsignmentById,
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getDressingRanges,
  getLabels,
  getProperties,
  getSexes,
  getWeightRanges,
  selectActiveProducts,
  selectSpeciesAttributeOptionsBySpeciesId,
} from "selectors";

import { TransactionalSaleFormValues } from "../lib/types";
import { FormRow } from "./FormRow";
import { Accreditation } from "constants/draftingAttributes";

interface ReviewTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleNamespace?: string;
}

export const ReviewTab = ({
  deploymentId,
  speciesId,
  saleNamespace,
}: ReviewTabProps) => {
  const { values } = useFormikContext<TransactionalSaleFormValues>();

  const businessByIdLookup = useSelector(getBusinesses);
  const propertiesLookup = useSelector(getProperties);
  const labelsLookup = useSelector(getLabels);
  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );
  const sexes = useSelector(getSexes);
  const products = useSelector(selectActiveProducts);

  const { saleLot, skinLots, sale } = values;

  // Vendor can come from consignment namespace (create flow) or be looked up
  // from the store via saleLot.consignment_id (edit lot flow).
  const consignmentFromStore = useSelector(
    getConsignmentById(saleLot.consignment_id),
  );
  const vendorId =
    values.consignment?.vendor_id ?? consignmentFromStore?.vendor_id ?? null;
  const vendorPropertyId =
    values.consignment?.vendor_property_id ??
    consignmentFromStore?.vendor_property_id ??
    null;
  const billingReference = values.consignment?.billing_reference ?? null;
  const date = sale?.date;
  const saleTitle = sale?.sale_title;

  const {
    pricing_type_id: pricingTypeId,
    product_id: productId,
    age_id: ageId,
    breed_id: breedId,
    sex_id: sexId,
    buyer_id: buyerId,
    buyer_way: buyerWay,
    destination_property_id: destinationPropertyId,
    invoiceToBusinessId,
    quantity,
    quantity_progeny: quantityProgeny,
    unit_price: unitPrice,
    total_mass_grams: totalMassGrams,
    quantity_delivered: quantityDelivered,
    dentitionId,
    marks,
    labels,
    note,
    reservePrice,
    draftingAttributes,
    category_id: categoryId,
    exemption_id: exemptionId,
    estimatedAverageWeightId,
    dressingRangeId,
  } = saleLot;

  const dentitionOptions = useSelector(getDentitionOptionsByAgeId(ageId));
  const dentitionLabel = dentitionOptions.find(
    d => d.value === dentitionId,
  )?.label;

  const categories = useSelector(getCategories);
  const categoryLabel = categoryId
    ? categories[categoryId]?.name || null
    : null;

  const weightRanges = useSelector(getWeightRanges);
  const weightRangeLabel = estimatedAverageWeightId
    ? weightRanges[estimatedAverageWeightId]?.shortCode || null
    : null;

  const dressingRanges = useSelector(getDressingRanges);
  const dressingRange = dressingRangeId
    ? dressingRanges[dressingRangeId]
    : null;
  const dressingRangeLabel = dressingRange
    ? dressingRange.dressingRangeMaxPercent
      ? `${parseInt(dressingRange.dressingRangeMinPercent, 10)}-${parseInt(dressingRange.dressingRangeMaxPercent, 10)}%`
      : `${dressingRange.dressingRangeMinPercent}+%`
    : null;

  const speciesAttributeOptionsBySpeciesId = useSelector(
    selectSpeciesAttributeOptionsBySpeciesId,
  );
  const exemptionOptions =
    speciesId != null
      ? speciesAttributeOptionsBySpeciesId[speciesId]?.exemptions ?? []
      : [];
  const exemptionLabel = exemptionId
    ? exemptionOptions.find(e => e.value === exemptionId)?.label || null
    : null;

  const vendorLabel = vendorId
    ? businessByIdLookup[vendorId]?.name || "-"
    : "Not Set";
  const vendorPropertyLabel = vendorPropertyId
    ? propertiesLookup[vendorPropertyId]?.PIC || "-"
    : null;

  const buyerLabel = buyerId
    ? businessByIdLookup[buyerId]?.name || "-"
    : "Not Set";
  const buyerPropertyLabel = destinationPropertyId
    ? propertiesLookup[destinationPropertyId]?.PIC || "-"
    : null;
  const buyerWayLabel = buyerWay?.name || null;

  const invoiceToLabel = invoiceToBusinessId
    ? businessByIdLookup[invoiceToBusinessId]?.name || "-"
    : null;

  const ageLabel = ageOptions.find(a => a.value === ageId)?.label;
  const breedLabel = breedOptions.find(b => b.value === breedId)?.label || "-";
  const sexLabel = sexId ? sexes[sexId]?.name || "-" : null;
  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);
  const productLabel = productId ? products[productId]?.name || "-" : null;

  const pricingLabel =
    LivestockSalePricingTypeDisplayNameLkp[pricingTypeId] || "-";

  const markNames = marks.map(mark => mark?.location).filter(Boolean);

  const labelNames = labels
    .map(id => labelsLookup[id]?.name || id)
    .filter(Boolean);

  return (
    <>
      <Column gap={0} style={{ paddingTop: 8 }}>
        {/* WHO */}
        <ReviewCard>
          <ReviewCardTitle>Who</ReviewCardTitle>

          <ReviewFieldGrid>
            <ReviewField xs={6} label="Vendor">
              {vendorLabel}
            </ReviewField>
            {vendorPropertyLabel && (
              <ReviewField xs={6} label="Vendor PIC">
                {vendorPropertyLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>

          <ReviewFieldGrid>
            <ReviewField xs={6} label="Buyer">
              {buyerLabel}
            </ReviewField>
            {buyerPropertyLabel && (
              <ReviewField xs={6} label="Buyer PIC">
                {buyerPropertyLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>

          {!saleNamespace && buyerWayLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={6} label="Buyer Way">
                {buyerWayLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}
        </ReviewCard>

        {/* WHAT */}
        <ReviewCard>
          <ReviewCardTitle>What</ReviewCardTitle>

          {productLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Description">
                {productLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          <ReviewFieldGrid>
            <ReviewField xs={6} label="Head">
              {quantity || "-"}
            </ReviewField>
            {sexLabel && (
              <ReviewField xs={6} label="Sex">
                {sexLabel}
              </ReviewField>
            )}
            {ageLabel && (
              <ReviewField xs={6} label="Age">
                {ageLabel}
              </ReviewField>
            )}
            <ReviewField xs={6} label="Breed">
              {breedLabel}
            </ReviewField>
            {selectedSexHasProgeny && quantityProgeny > 0 && (
              <ReviewField xs={6} label="Progeny">
                {quantityProgeny}
              </ReviewField>
            )}
            {dentitionLabel && (
              <ReviewField xs={6} label="Dentition">
                {dentitionLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>

          {skinLots?.some(lot => lot.quantity) && (
            <>
              {skinLots.map(
                ({ quantity: skinQuantity, unitPrice: skinUnitPrice }, i) => (
                  <ReviewFieldGrid key={i}>
                    <ReviewField
                      xs={6}
                      label={
                        skinLots.length > 1
                          ? `Skins Lot ${i + 1} Qty`
                          : "Skins Qty"
                      }
                    >
                      {skinQuantity || "-"}
                    </ReviewField>
                    <ReviewField
                      xs={6}
                      label={
                        skinLots.length > 1
                          ? `Skins Lot ${i + 1} Price`
                          : "Skins Price"
                      }
                    >
                      {skinUnitPrice ? `$${skinUnitPrice}` : "-"}
                    </ReviewField>
                  </ReviewFieldGrid>
                ),
              )}
              {skinLots.length > 1 && (
                <ReviewFieldGrid>
                  <ReviewField xs={6} label="Total Skins Qty">
                    {skinLots.reduce(
                      (sum, { quantity: lotQty }) => sum + (lotQty || 0),
                      0,
                    )}
                  </ReviewField>
                  <ReviewField xs={6} label="Total Skins Value">
                    {`$${(
                      skinLots.reduce(
                        (sum, skinsLot) =>
                          sum + (calculateTotalPriceCents(skinsLot) || 0),
                        0,
                      ) / 100
                    ).toFixed(2)}`}
                  </ReviewField>
                </ReviewFieldGrid>
              )}
              {billingReference && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Kill ID / Ref">
                    {billingReference}
                  </ReviewField>
                </ReviewFieldGrid>
              )}
            </>
          )}

          {markNames.length > 0 && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Marks">
                {markNames.join(", ")}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          {labelNames.length > 0 && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Labels">
                {labelNames.join(", ")}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          {draftingAttributes?.animalName && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Animal Name">
                {draftingAttributes.animalName}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          {note && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Note">
                {note}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          {(weightRangeLabel || dressingRangeLabel) && (
            <ReviewFieldGrid>
              {weightRangeLabel && (
                <ReviewField xs={6} label="Est Avg Weight">
                  {weightRangeLabel}
                </ReviewField>
              )}
              {dressingRangeLabel && (
                <ReviewField xs={6} label="Est Dressing %">
                  {dressingRangeLabel}
                </ReviewField>
              )}
            </ReviewFieldGrid>
          )}

          {categoryLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={6} label="Category">
                {categoryLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}

          {exemptionLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="EID Exemption">
                {exemptionLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}
        </ReviewCard>

        {/* ADVANCED DRAFTING */}
        {draftingAttributes &&
          (() => {
            const da = draftingAttributes as DraftingAttributes;

            const breeds = [1, 2, 3]
              .map(i => ({
                label: breedOptions.find(
                  b => b.value === da[`advancedBreed${i}Id`],
                )?.label,
                hd: da[`advancedBreed${i}Hd`],
              }))
              .filter(b => b.label || b.hd);

            const hornEntries: [string, number][] = (
              [
                ["Polled", da.hornPolled],
                ["Horned", da.hornHorned],
                ["Dehorned", da.hornDehorned],
                ["Tipped", da.hornTipped],
                ["Scurred", da.hornScurred],
              ] as [string, number][]
            ).filter(([, v]) => v != null);

            const dentitionEntries: [string, number][] = (
              [
                ["Milk/Lamb", da.dentition0],
                ["2T", da.dentition2],
                ["4T", da.dentition4],
                ["6T", da.dentition6],
                ["8T", da.dentition8],
                ["Broken", da.dentitionBroken],
              ] as [string, number][]
            ).filter(([, v]) => v != null);

            const fatScoreEntries: [string, number][] = [1, 2, 3, 4, 5, 6]
              .map(i => [`Score ${i}`, da[`fatScore${i}`]] as [string, number])
              .filter(([, v]) => v != null);

            const muscleScoreEntries: [string, number][] = [1, 2, 3, 4, 5]
              .map(
                i => [`Score ${i}`, da[`muscleScore${i}`]] as [string, number],
              )
              .filter(([, v]) => v != null);

            const accreditationFields: Record<string, string> = {
              accreditationEU: "EU",
              accreditationPTIC: "PTIC",
              accreditationPTE: "PTE",
              accreditationPCAS: "PCAS",
              accreditationAntibioticFree: "Antibiotic Free",
              accreditationOrganic: "Organic",
              accreditationGrassFed: "Grass Fed",
              stationMated: "Station Mated",
              notStationMated: "Not Station Mated",
              accreditationBackgrounded: "Backgrounded",
              feeder: "Feeder",
              animalHealthStatementOverride: "AHS",
              sixInOne: "6 in 1",
              drenched: "Drenched",
              ojdVac: "OJD Vac",
              gudairApproved: "Gudair Approved",
              b12Vac: "B12 Vac",
              backline: "Backline/Dipped",
              MAndTS: "M&TS",
              SAEligible: "SA Eligible",
              painRelief: "Pain Relief",
            };
            const setAccreditations = Object.entries(accreditationFields)
              .filter(
                ([key]) => da[key] === Accreditation.ALL || da[key] === true,
              )
              .map(([, label]) => label);

            const conditions = [
              da.conditionStore && "Store",
              da.conditionForward && "Forward",
              da.conditionPrime && "Prime",
            ].filter(Boolean) as string[];

            const hasAnyData =
              breeds.length > 0 ||
              hornEntries.length > 0 ||
              dentitionEntries.length > 0 ||
              fatScoreEntries.length > 0 ||
              muscleScoreEntries.length > 0 ||
              da.frameStructure ||
              da.accreditationJBAS ||
              setAccreditations.length > 0 ||
              conditions.length > 0 ||
              da.publicDescription ||
              da.publicFaultDescription ||
              da.partOfRun ||
              da.pregStatus ||
              da.joinedStart ||
              da.shearingStatus ||
              da.bloodline ||
              da.xBloodline ||
              da.vendorBredOverride ||
              da.ageRangeTimeUnit ||
              da.maxAge ||
              da.minAge ||
              da.tagNumber;

            if (!hasAnyData) return null;

            return (
              <ReviewCard>
                <ReviewCardTitle>Advanced Drafting</ReviewCardTitle>

                {breeds.map((b, i) => (
                  <ReviewFieldGrid key={i}>
                    <ReviewField xs={8} label={`Breed ${i + 1}`}>
                      {b.label || "-"}
                    </ReviewField>
                    {b.hd != null && (
                      <ReviewField xs={4} label="Hd">
                        {b.hd}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                ))}

                {(da.bloodline || da.xBloodline || da.bySire) && (
                  <ReviewFieldGrid>
                    {da.bloodline && (
                      <ReviewField xs={4} label="Bloodline">
                        {da.bloodline}
                      </ReviewField>
                    )}
                    {da.xBloodline && (
                      <ReviewField xs={4} label="X-Bloodline">
                        {da.xBloodline}
                      </ReviewField>
                    )}
                    {da.bySire && (
                      <ReviewField xs={4} label="By - Sire">
                        {da.bySire}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                )}

                {da.ageRangeTimeUnit && (da.minAge || da.maxAge) && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Age Range">
                      {`${da.minAge} - ${da.maxAge} ${da.ageRangeTimeUnit}`}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {hornEntries.length > 0 && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Horn (hd)">
                      {hornEntries.map(([l, v]) => `${l}: ${v}`).join(", ")}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {(da.isNotMouthed || dentitionEntries.length > 0) && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Dentition (hd)">
                      {[
                        da.isNotMouthed ? "Not Mouthed" : null,
                        ...dentitionEntries.map(([l, v]) => `${l}: ${v}`),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {fatScoreEntries.length > 0 && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Fat Score (hd)">
                      {fatScoreEntries.map(([l, v]) => `${l}: ${v}`).join(", ")}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {muscleScoreEntries.length > 0 && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Muscle Score (hd)">
                      {muscleScoreEntries
                        .map(([l, v]) => `${l}: ${v}`)
                        .join(", ")}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {da.frameStructure && (
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Frame Structure">
                      {da.frameStructure}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {(setAccreditations.length > 0 ||
                  da.accreditationJBAS ||
                  da.vendorBredOverride) && (
                  <ReviewFieldGrid>
                    {setAccreditations.length > 0 && (
                      <ReviewField xs={12} label="Accreditations">
                        {setAccreditations.join(", ")}
                      </ReviewField>
                    )}
                    {!!da.accreditationJBAS && (
                      <ReviewField xs={6} label="JBAS">
                        {da.accreditationJBAS}
                      </ReviewField>
                    )}
                    {!!da.vendorBredOverride && (
                      <ReviewField xs={6} label="Vendor Bred">
                        {da.vendorBredOverride}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                )}

                {conditions.length > 0 && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Condition">
                      {conditions.join(", ")}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {da.pregStatus && (
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Preg Status">
                      {da.pregStatus}
                    </ReviewField>
                    {da.sil != null && (
                      <ReviewField xs={6} label="SIL">
                        {da.sil}%
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                )}

                {(da.joinedStart || da.joinedEnd) && (
                  <ReviewFieldGrid>
                    {da.joinedStart && (
                      <ReviewField xs={6} label="Joined Start">
                        {da.joinedStart}
                      </ReviewField>
                    )}
                    {da.joinedEnd && (
                      <ReviewField xs={6} label="Joined End">
                        {da.joinedEnd}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                )}

                {(da.shearingStatus || da.shearing) && (
                  <ReviewFieldGrid>
                    {da.shearingStatus && (
                      <ReviewField xs={6} label="Shearing Status">
                        {da.shearingStatus}
                      </ReviewField>
                    )}
                    {da.shearing && (
                      <ReviewField xs={6} label="Shearing Date">
                        {da.shearing}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                )}

                {da.publicDescription && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Comments">
                      {da.publicDescription}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {da.publicFaultDescription && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Fault Description">
                      {da.publicFaultDescription}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {da.partOfRun && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Part of Run">
                      {da.partOfRun}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

                {da.tagNumber && (
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Tag #">
                      {da.tagNumber}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}
              </ReviewCard>
            );
          })()}

        {/* WHEN - only in create-sale flow */}
        {saleNamespace && date && (
          <ReviewCard>
            <ReviewCardTitle>When</ReviewCardTitle>
            <ReviewFieldGrid>
              <ReviewField xs={6} label="Sale Date">
                {date}
              </ReviewField>
            </ReviewFieldGrid>
          </ReviewCard>
        )}

        {/* PRICE */}
        <ReviewCard>
          <ReviewCardTitle>Price</ReviewCardTitle>
          <ReviewFieldGrid>
            <ReviewField xs={6} label="Pricing">
              {pricingLabel}
            </ReviewField>
            <ReviewField xs={6} label="Unit Price">
              {unitPrice || "-"}
            </ReviewField>
            {totalMassGrams > 0 && (
              <ReviewField xs={6} label="Total Mass">
                {formatWeightKg(totalMassGrams, true)}
              </ReviewField>
            )}
            {reservePrice && (
              <ReviewField xs={6} label="Reserve Price">
                {`${formatDollarsFromCents(reservePrice)}`}
              </ReviewField>
            )}
            {quantityDelivered && (
              <ReviewField xs={6} label="Hd Delivered">
                {quantityDelivered}
              </ReviewField>
            )}
          </ReviewFieldGrid>
          {invoiceToLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Invoice To">
                {invoiceToLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}
        </ReviewCard>
      </Column>

      {/* Sale-level fields - only in create-sale flow */}
      {saleNamespace && (
        <ReviewCard>
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Sale Title">
              {saleTitle || "-"}
            </ReviewField>
          </ReviewFieldGrid>
          <FormRow>
            <Grid item xs={12}>
              <TextArea
                name={withNamespace(saleNamespace, "notes")}
                label="Sale Notes"
                rows={4}
              />
            </Grid>
          </FormRow>
        </ReviewCard>
      )}
    </>
  );
};
